import { FLOPFLIP_STATE_SLICE } from '@flopflip/react-redux';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { FeatureFlag } from 'constants/featureFlags';
import { LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { buildPendingChangesAuthorsHeaderValue } from 'utils/pendingChangesAuthors';

import { getUuid } from 'store/auth/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';

const emptySelectionState = {
  activeApplicationId: null,
  authorKeysByApplicationId: {},
  aiRunsByExecutionId: {},
};

const noLoanActions = [];

const selectPendingChangesAuthorsSelectionState = ({ loanApplication }) =>
  loanApplication?.pendingChangesAuthorsSelection ?? emptySelectionState;

const selectLoanActionsSafe = ({ loanApplication }) =>
  loanApplication?.loanActions ?? noLoanActions;

const selectIsSavePendingChangesLoanActionEnabled = state =>
  !!state?.[FLOPFLIP_STATE_SLICE] &&
  !!selectFeatureFlagByKey(FeatureFlag.EnableSavePendingChangesLoanAction)(state);

export const selectCurrentUserIdSafe = state => (state?.auth?.master ? getUuid(state) : null);

export const selectIsPendingChangesByUserEnabled = state =>
  !!state?.[FLOPFLIP_STATE_SLICE] &&
  !!selectFeatureFlagByKey(FeatureFlag.PendingChangesByUser)(state);

export const selectPendingChangesAiRunsByExecutionId = createSelector(
  selectPendingChangesAuthorsSelectionState,
  ({ aiRunsByExecutionId }) => aiRunsByExecutionId,
);

export const selectActivePendingChangesAuthorsApplicationId = createSelector(
  selectPendingChangesAuthorsSelectionState,
  ({ activeApplicationId }) => activeApplicationId,
);

/**
 * "View other users' changes" on the Save Pending Changes loan action. Only that action can turn it off: with the
 * loan-action flag off, no such action listed for the user, or a listing that predates the option, everyone's
 * changes stay selectable as they are today.
 */
export const selectIsViewOthersPendingChangesAllowed = createSelector(
  selectIsSavePendingChangesLoanActionEnabled,
  selectLoanActionsSafe,
  (isLoanActionEnabled, loanActions) => {
    if (!isLoanActionEnabled) {
      return true;
    }

    const savePendingChangesActionType = loanActions
      .flatMap(({ loanActionTypes }) => loanActionTypes ?? [])
      .find(({ type }) => type === LoanEditorActionsEnum.SAVE_PENDING_CHANGES);

    return savePendingChangesActionType?.grantedViewOthers !== false;
  },
);

export const selectSelectedPendingChangesAuthorKeys = createSelector(
  selectPendingChangesAuthorsSelectionState,
  selectCurrentUserIdSafe,
  selectIsViewOthersPendingChangesAllowed,
  ({ activeApplicationId, authorKeysByApplicationId }, currentUserId, isViewOthersAllowed) => {
    const authorKeys = authorKeysByApplicationId[activeApplicationId] ?? _.compact([currentUserId]);

    return isViewOthersAllowed ? authorKeys : authorKeys.filter(key => key === currentUserId);
  },
);

export const selectPendingChangesAuthorsHeaderValue = createSelector(
  selectIsPendingChangesByUserEnabled,
  selectActivePendingChangesAuthorsApplicationId,
  selectSelectedPendingChangesAuthorKeys,
  (isEnabled, activeApplicationId, authorKeys) =>
    isEnabled && !_.isNil(activeApplicationId)
      ? buildPendingChangesAuthorsHeaderValue(authorKeys)
      : null,
);

export const selectIsCurrentUserPendingChangesAuthorSelected = createSelector(
  selectSelectedPendingChangesAuthorKeys,
  selectCurrentUserIdSafe,
  (authorKeys, currentUserId) => authorKeys.includes(currentUserId),
);

/**
 * Field-highlight filter for the loan changes history: null keeps today's behavior (every author
 * highlights), otherwise only the selected authors' changes highlight.
 */
export const selectPendingChangesHighlightAuthorKeys = createSelector(
  selectIsPendingChangesByUserEnabled,
  selectSelectedPendingChangesAuthorKeys,
  (isEnabled, authorKeys) => (isEnabled ? authorKeys : null),
);
